import { Box } from '@chakra-ui/react';
import * as React from 'react';

type SentinelProps = {
  /**
   *  A function to execute when the element enters the page
   */
  onIntersection: () => void;

  /**
   * Controls the effective margin of the element. You can use this allows you
   * to create an invisible "offset" so that the element will intersect sooner
   */
  rootMargin?: string;
};

/**
 * Renders an element with an intersection observer attached
 *
 * When you add a <Sentinel /> on the page, it's onIntersection() function
 * will execute when the sentinel crosses into the visible portion of the
 * screen. We can use this to fetch more data for infinite scroll, trigger
 * incoming animations as the user scrolls down, lazy load things, etc.
 */
export const Sentinel: React.FC<SentinelProps> = ({
  onIntersection,
  rootMargin,
}) => {
  const elementRef = React.useRef<HTMLDivElement>(null);

  const onIntersectionRef = React.useRef(onIntersection);

  React.useLayoutEffect(() => {
    onIntersectionRef.current = onIntersection;
  });

  React.useEffect(() => {
    const callback: IntersectionObserverCallback = ([entry]) => {
      if (entry.isIntersecting) {
        onIntersectionRef.current();
      }
    };

    const intersectionObserver = new IntersectionObserver(callback, {
      rootMargin,
    });

    if (elementRef.current) {
      intersectionObserver.observe(elementRef.current);
    }

    return () => {
      intersectionObserver.disconnect();
    };
  }, [rootMargin]);

  return <Box ref={elementRef} />;
};
